import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { NgxMaskPipe } from 'ngx-mask';
import { AnalyticsService } from '../../../services/analytics.service';
import { LeadsStateService } from '../../../services/leadState.service';
import { DateUtilService } from '../../../services/dateUtil.service';
import { CadastroService } from '../../../services/cadastro.service';
import { AuthService } from '../../../services/auth.service';
import { Lead } from '../../../models/analytics.model';
import { Cliente, Page } from '../../../models/cadastro.model';
import { ConfirmDialogComponent } from '../../../pages/modal/confirm-dialog/confirm-dialog';
import { LeadModalComponent } from '../../../pages/modal/lead-modal/lead-modal.component';

@Component({
  selector: 'app-leads-do-dia',
  standalone: true,
  imports: [CommonModule, NgxMaskPipe, ConfirmDialogComponent, LeadModalComponent],
  templateUrl: './leads-do-dia.component.html',
  styleUrl: './leads-do-dia.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LeadsDoDialComponent implements OnInit {

  leadsPage: Page<Lead> | undefined;
  allLeads: Lead[] = [];
  termoBuscaLeads = '';
  isLoading = true;
  hasError = false;

  currentPage = 0;
  itemsPerPage = 10;
  readonly backendFetchSize = 100;

  isConfirmDialogOpen = false;
  leadSelecionadoParaRemocao: Lead | null = null;

  isLeadModalOpen = false;
  leadSelecionadoId: number | null = null;

  protected readonly leadsState = inject(LeadsStateService);
  private readonly cdr = inject(ChangeDetectorRef);

  constructor(
    private toastr: ToastrService,
    private analyticsService: AnalyticsService,
    private cadastroService: CadastroService,
    private router: Router,
    private dateUtilService: DateUtilService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.leadsState.refreshTotalLeads();
    this.loadLeads();
  }

  loadLeads(): void {
    this.isLoading = true;
    this.hasError = false;
    this.cdr.detectChanges();

    this.analyticsService.leadsPorIntervaloDatas(
      this.dateUtilService.sevenDaysAgo,
      this.dateUtilService.dataHoje,
      0,
      this.backendFetchSize
    ).subscribe({
      next: data => {
        this.leadsPage = data;
        this.allLeads = data.content ?? [];
        this.currentPage = 0;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.hasError = true;
        this.isLoading = false;
        this.toastr.error('Não foi possível carregar os Leads do dia.');
        this.cdr.detectChanges();
      }
    });
  }



  classificarEAtualizarLeads(): void {
    this.analyticsService.classificarLeads().subscribe({
      next: () => {
        this.toastr.success('Leads classificados com sucesso.');
        this.loadLeads();
      },
      error: () => {
        this.toastr.error('Não foi possível classificar os Leads.');
      }
    });

  }

  setPage(page: number): void {
    if (page < 0 || page >= this.totalPagesLeads) return;
    this.currentPage = page;
    this.cdr.detectChanges();
  }

  atualizarBuscaLeads(valor: string): void {
    this.termoBuscaLeads = valor;
    this.currentPage = 0;
    this.cdr.detectChanges();
  }

  get leadsFiltrados(): Lead[] {
    const termo = this.normalizarTexto(this.termoBuscaLeads);
    if (!termo) return this.allLeads;
    return this.allLeads.filter(lead => this.normalizarTexto(lead.nome).includes(termo));
  }

  get leadsPaginados(): Lead[] {
    const inicio = this.currentPage * this.itemsPerPage;
    return this.leadsFiltrados.slice(inicio, inicio + this.itemsPerPage);
  }

  get totalPagesLeads(): number {
    const total = Math.ceil(this.leadsFiltrados.length / this.itemsPerPage);
    return total > 0 ? total : 1;
  }

  get pageRange(): number[] {
    const total = this.totalPagesLeads;
    const current = this.currentPage;
    const delta = 2;
    const start = Math.max(0, current - delta);
    const end = Math.min(total - 1, current + delta);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  efetuarOrcamento(lead: Lead): void {
    this.cadastroService.getIdClienteByTelefone(lead.telefone).subscribe({
      next: clienteId => {
        if (clienteId === null) {
          const newCliente: Cliente = {
            id: 0,
            nomeRazaoSocial: lead.nome,
            nomeFantasia: '',
            cpfCnpj: '00000000000',
            endereco: '',
            municipio: '',
            uf: 'SP',
            pais: 'Brasil',
            email: lead.email,
            telefone: lead.telefone,
            telefone2: '',
            observacao: 'Cadastro via lead',
            restricaoAtiva: false
          };
          this.cadastroService.createCliente(newCliente).subscribe({
            next: cliente => this.router.navigate(
              ['/home', 'venda-orcamento-item', cliente.id, 'conversao'],
              { queryParams: { origem: 'homepage' } }
            ),
            error: () => this.toastr.error('Não foi possível converter este Lead.')
          });
        } else {
          this.router.navigate(
            ['/home', 'venda-orcamento-item', clienteId, 'conversao'],
            { queryParams: { origem: 'homepage' } }
          );
        }
      },
      error: () => this.toastr.error('Não foi possível converter este Lead.')
    });
  }

  visualizarLead(lead: Lead): void {
    this.leadSelecionadoId = lead.id;
    this.isLeadModalOpen = true;
    this.cdr.detectChanges();
  }

  fecharLeadModal(): void {
    this.isLeadModalOpen = false;
    this.leadSelecionadoId = null;
    this.cdr.detectChanges();
  }

  removerLead(lead: Lead): void {
    this.leadSelecionadoParaRemocao = lead;
    this.isConfirmDialogOpen = true;
    this.cdr.detectChanges();
  }

  fecharDialogRemocaoLead(): void {
    this.isConfirmDialogOpen = false;
    this.leadSelecionadoParaRemocao = null;
    this.cdr.detectChanges();
  }

  confirmarRemocaoLead(): void {
    const lead = this.leadSelecionadoParaRemocao;
    if (!lead?.id) { this.fecharDialogRemocaoLead(); return; }

    this.analyticsService.deleteLead(lead.id).subscribe({
      next: () => {
        this.allLeads = this.allLeads.filter(item => item.id !== lead.id);
        if (this.currentPage >= this.totalPagesLeads) {
          this.currentPage = Math.max(0, this.totalPagesLeads - 1);
        }
        this.leadsState.refreshTotalLeads();
        this.toastr.success('Lead removido com sucesso.');
        this.fecharDialogRemocaoLead();
      },
      error: () => {
        this.toastr.error('Nao foi possivel remover o lead.');
        this.fecharDialogRemocaoLead();
      }
    });
  }

  private normalizarTexto(valor: string | null | undefined): string {
    return (valor ?? '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .trim();
  }
}
